import { Crud } from "@/api";
import { UserApi } from "@/api/user.api";
import CartBadge from "@/components/CartBadge/CartBadge";
import { CartLineItem } from "@/components/CartLineItem/CartLineItem";
import { useUploadModal } from "@/components/UploadModal/useUploadModal";
import { CartPdf } from "@/pages/DashboardCart/CartPdf";
import { $account } from "@/stores/account";
import { $activeAdminCartId } from "@/stores/adminWorkspace";
import { $cart, CartService } from "@/stores/cart";
import { $checkout } from "@/stores/checkout";
import { formatPrice } from "@/utils/functions/formatPrice";
import { searchParentOrderOptions } from "@/utils/parentOrderOptions";
import { usePending } from "@/utils/hooks/usePending.js";
import { LineItem } from "@/utils/types/types";
import { PlusIcon, ShareIcon } from "@heroicons/react/24/solid";
import {
  Button,
  Divider,
  Flex,
  Loader,
  Select,
  Switch,
  Text,
  Textarea,
  TextInput,
} from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { useStore } from "@nanostores/react";
import { Clipboard, X } from "@phosphor-icons/react";
import { pdf } from "@react-pdf/renderer";
import { Fragment, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import CustomItemModal from "../CustomItemModal/CustomItemModal";

export default function Cart() {
  const account = useStore($account);
  const activeAdminCartId = useStore($activeAdminCartId);
  const cart = useStore($cart);
  const uploadModal = useUploadModal();
  const [deletingItemId, setDeletingItemId] = useState<string | null>(null);
  const [isCheckoutLoading, setIsCheckoutLoading] = useState(false);
  const [showCheckoutLink, setShowCheckoutLink] = useState(false);
  const [generatedCheckoutLink, setGeneratedCheckoutLink] = useState("");
  const [linkedParentOrderNo, setLinkedParentOrderNo] = useState("");
  const [parentOrderOptions, setParentOrderOptions] = useState<
    { value: string; label: string }[]
  >([]);
  const [linkCopied, setLinkCopied] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [messageValue, setMessageValue] = useState(cart.message || "");
  const messageDirty = messageValue !== (cart.message || "");
  const saveMessage = usePending(async () => {
    await CartService.update({ message: messageValue });
    setMessageValue(messageValue);
  });
  const isShareable =
    Boolean(activeAdminCartId) && activeAdminCartId === cart.id;
  const lineItems = cart.lineItems;
  const printCount = lineItems.reduce(
    (a: number, item) => a + item.quantity,
    0
  );
  const subtotal = lineItems.reduce(
    (a: number, item) => a + Number(item.itemJson.price || 0) * item.quantity,
    0
  );

  const [lineItemsToUpdate, setLineItemsToUpdate] = useState<
    Pick<LineItem, "id" | "quantity" | "note">[]
  >([]);
  const isPendingUpdate = lineItemsToUpdate.length > 0;
  const updateLineItem = usePending(async () => {
    if (!isPendingUpdate) return;
    await CartService.updateLineItems(lineItemsToUpdate);
    setLineItemsToUpdate([]);
  });

  const loadParentOrderOptions = usePending(async (q: string = "") => {
    try {
      setParentOrderOptions(await searchParentOrderOptions(q));
    } catch (error) {
      // Admin-only nicety inside the customer cart drawer: a failure here
      // must stay invisible to customers and must not go unhandled.
      // (Sentry TPW-ASTRO-WEB-UI-1M regression, Cart.tsx call site.)
      console.error("Error loading parent order options", error);
      (window as any).Sentry?.captureException(error);
    }
  });

  // Debounced server-side order search: every keystroke (300ms settled)
  // queries the backend across ALL root orders — no more 100-order window.
  const parentOrderSearchTimer = useRef<ReturnType<typeof setTimeout> | null>(
    null
  );
  const handleParentOrderSearch = (q: string) => {
    if (parentOrderSearchTimer.current) {
      clearTimeout(parentOrderSearchTimer.current);
    }
    parentOrderSearchTimer.current = setTimeout(() => {
      loadParentOrderOptions(q);
    }, 300);
  };

  // Keep the selected order visible in the dropdown even when later searches
  // return lists that don't contain it (otherwise the Select drops its label).
  const selectedParentOption = parentOrderOptions.find(
    o => o.value === linkedParentOrderNo
  );
  const [pinnedParentOption, setPinnedParentOption] = useState<{
    value: string;
    label: string;
  } | null>(null);
  const parentOrderSelectData =
    pinnedParentOption &&
    !parentOrderOptions.some(o => o.value === pinnedParentOption.value)
      ? [pinnedParentOption, ...parentOrderOptions]
      : parentOrderOptions;

  useEffect(() => {
    if (selectedParentOption) setPinnedParentOption(selectedParentOption);
  }, [linkedParentOrderNo]);

  useEffect(() => {
    CartService.getCart();
  }, []);

  // Parent-order options feed an ADMIN-ONLY dropdown (the isAdmin block
  // below). Loading them on mount fired a large orders search for EVERY
  // customer who opened the cart drawer — a request for a control they can
  // never see. Wait until the isAdmin check resolves, then load once.
  // Runbook open TODO #2; Sentry TPW-ASTRO-WEB-UI-1M (87 events, all live
  // newtemp traffic). 2026-07-25.
  useEffect(() => {
    if (!isAdmin) return;
    loadParentOrderOptions();
  }, [isAdmin]);

  useEffect(() => {
    setMessageValue(cart.message || "");
  }, [cart.message]);

  useEffect(() => {
    if (!account.isLoggedIn) {
      setIsAdmin(false);
      return;
    }

    let isMounted = true;
    (async () => {
      try {
        const res = await UserApi.getUser();
        if (!isMounted) return;
        setIsAdmin(Boolean(res?.data?.user?.isAdmin));
      } catch {
        if (!isMounted) return;
        setIsAdmin(false);
      }
    })();

    return () => {
      isMounted = false;
    };
  }, [account.isLoggedIn]);

  const [
    customItemModalOpened,
    { open: openCustomItemModal, close: closeCustomItemModal },
  ] = useDisclosure(false);

  const toggleCheckoutLink = usePending(async () => {
    if (showCheckoutLink) {
      setShowCheckoutLink(false);
      return;
    }

    if (!lineItems.length) return;

    // Short link (2026-09-03): server mints secret + short code together.
    const { url } = await Crud.carts.shareLink(cart.id, linkedParentOrderNo);
    await CartService.getCart();
    setGeneratedCheckoutLink(url);
    setShowCheckoutLink(true);
  });

  const toggleTaxExempt = usePending((checked: boolean) =>
    Crud.carts
      .update(cart.id, { taxExempt: checked } as any)
      .then(() => CartService.getCart())
  );

  const toggleSkipShipping = usePending((checked: boolean) =>
    Crud.carts
      .update(cart.id, { skipShipping: checked } as any)
      .then(() => CartService.getCart())
  );

  return (
    <div
      className={`flex h-full flex-col gap-6 bg-gray-0 px-6 py-8 md:overflow-auto`}
    >
      <CustomItemModal
        opened={customItemModalOpened}
        onClose={() => {
          closeCustomItemModal();
          CartService.getCart();
        }}
        cartId={cart.id}
      />
      <div className="flex items-center justify-between">
        <p className="text-[24px] font-[700]">
          {printCount} {printCount > 1 ? "Items" : "Item"}
        </p>
        {isAdmin && (
          <CartBadge
            isShareable={isShareable}
            cartId={cart?.id}
            cartName={(cart as any)?.name}
            rounded
          />
        )}
        <Button
          size="compact-sm"
          radius="full"
          color="dark"
          variant="light"
          style={{ width: 24, height: 24, padding: 0 }}
          onClick={() => CartService.toggleCart()}
        >
          <X />
        </Button>
      </div>

      {/* Cart Items */}
      <div className="flex flex-col gap-4 overflow-auto">
        {lineItems.map((lineItem, index) => (
          <Fragment key={lineItem.id}>
            {index !== 0 && <Divider />}
            <CartLineItem
              onDelete={async id => {
                setDeletingItemId(id);
                await CartService.removeLineItem(id);
                setDeletingItemId(null);
              }}
              lineItem={lineItem}
              onUpdate={({ id, quantity, note }) => {
                const items = lineItemsToUpdate
                  .filter(el => el.id !== id)
                  .concat({ id, quantity: quantity, note: note });
                setLineItemsToUpdate(items);
              }}
              deleting={deletingItemId === lineItem.id}
              isAdmin={isAdmin}
              showInsert
              onSaved={() => CartService.getCart()}
            />
          </Fragment>
        ))}
      </div>

      {/* Message Field */}
      <div className="flex flex-col gap-2">
        <label className="text-sm font-semibold">Message</label>
        <Textarea
          placeholder="Add message..."
          value={messageValue}
          onChange={e => setMessageValue(e.target.value)}
          minRows={2}
          maxRows={4}
        />
        {messageDirty && (
          <Button
            size="xs"
            onClick={() => saveMessage()}
            loading={saveMessage.isPending}
          >
            Save Message
          </Button>
        )}
      </div>

      {/* Footer with Checkout Button */}
      <div className="mt-auto grid gap-2">
        {isAdmin ? (
          <>
            <div className="flex w-fit flex-col gap-2 pt-2">
              <button
                className="text-left text-sm text-[#4AA0A0] underline transition-opacity hover:opacity-70"
                onClick={async () => {
                  const toBase64 = async (url: string): Promise<string> => {
                    const res = await fetch(url);
                    const blob = await res.blob();
                    return new Promise<string>(resolve => {
                      const reader = new FileReader();
                      reader.onload = () => resolve(reader.result as string);
                      reader.readAsDataURL(blob);
                    });
                  };

                  const imageMap: Record<string, string> = {};
                  await Promise.all(
                    cart.lineItems.map(async item => {
                      const imgUrl =
                        item.itemJson.compressedImageUrl ||
                        item.itemJson.imageUrl;
                      if (!imgUrl) return;
                      try {
                        imageMap[item.id] = await toBase64(imgUrl);
                      } catch {
                        // image unreachable — will fall back to placeholder
                      }
                    })
                  );

                  let logoBase64: string | undefined;
                  try {
                    logoBase64 = await toBase64("/logo.png");
                  } catch {
                    // logo unreachable — will fall back to text
                  }

                  const blob = await pdf(
                    <CartPdf
                      cart={cart as any}
                      imageMap={imageMap}
                      logoBase64={logoBase64}
                    />
                  ).toBlob();
                  const url = URL.createObjectURL(blob);
                  const a = document.createElement("a");
                  a.href = url;
                  const cartAsAny = cart as any;
                  a.download = `cart-${cartAsAny.name ? cartAsAny.name.replace(/\s+/g, "-") : cart.id}.pdf`;
                  a.click();
                  URL.revokeObjectURL(url);
                }}
              >
                Download PDF
              </button>
              <Button
                onClick={uploadModal.open}
                color="gray.5"
                radius="md"
                size="sm"
              >
                Create another print!
              </Button>
              {isShareable && (
                <>
                  <Select
                    data={parentOrderSelectData}
                    value={linkedParentOrderNo || null}
                    onChange={value => setLinkedParentOrderNo(value || "")}
                    searchable
                    clearable
                    placeholder="Search any order # or email"
                    size="xs"
                    onSearchChange={handleParentOrderSearch}
                    // Options are already server-filtered — a second local
                    // filter pass would hide matches (e.g. email searches).
                    filter={({ options }) => options}
                    nothingFoundMessage={
                      loadParentOrderOptions.isPending
                        ? "Searching…"
                        : "No orders found"
                    }
                    comboboxProps={{ withinPortal: true, zIndex: 9000 }}
                    rightSection={
                      loadParentOrderOptions.isPending ? (
                        <Loader size="xs" />
                      ) : undefined
                    }
                    rightSectionPointerEvents="none"
                  />
                  <Button
                    radius="md"
                    size="sm"
                    leftSection={<PlusIcon className="size-4" />}
                    onClick={openCustomItemModal}
                  >
                    Create Custom Item
                  </Button>
                  <Button
                    color="indigo"
                    radius="md"
                    size="sm"
                    leftSection={<ShareIcon className="size-4" />}
                    onClick={toggleCheckoutLink}
                    loading={toggleCheckoutLink.isPending}
                    disabled={!lineItems.length}
                  >
                    {showCheckoutLink
                      ? "Hide checkout link"
                      : "Create checkout link"}
                  </Button>
                </>
              )}
            </div>
            {showCheckoutLink && generatedCheckoutLink && (
              <div className="mt-3 rounded-md border border-[#00000014] bg-white p-3">
                <p className="mb-2 text-sm font-medium text-[#191919]">
                  Checkout Link
                </p>
                <div className="flex items-center gap-2">
                  <TextInput
                    value={generatedCheckoutLink}
                    readOnly
                    style={{ flex: 1 }}
                  />
                  <Button
                    variant="light"
                    color={linkCopied ? "teal" : undefined}
                    leftSection={<Clipboard size={16} />}
                    onClick={() => {
                      navigator.clipboard.writeText(generatedCheckoutLink);
                      setLinkCopied(true);
                      setTimeout(() => setLinkCopied(false), 2000);
                    }}
                  >
                    {linkCopied ? "Copied!" : "Copy"}
                  </Button>
                </div>
              </div>
            )}
            <div className="flex flex-wrap items-end justify-between gap-2">
              <div className="flex flex-col gap-2 pt-2">
                <Switch
                  label="Turn off sales tax"
                  checked={!!(cart as any).taxExempt}
                  onChange={e => toggleTaxExempt(e.currentTarget.checked)}
                  color="#4AA0A0"
                  size="sm"
                />
                {/* 2026-10-12: never on a shipping-only cart — it exists to price shipping. */}
                {!(cart as any).shippingOnlyForOrderNo && (
                  <Switch
                    label="Turn off shipping calculations"
                    checked={!!(cart as any).skipShipping}
                    onChange={e => toggleSkipShipping(e.currentTarget.checked)}
                    color="#4AA0A0"
                    size="sm"
                  />
                )}
              </div>
              {isPendingUpdate ? (
                <Button
                  radius="md"
                  size="sm"
                  onClick={updateLineItem}
                  loading={updateLineItem.isPending}
                >
                  Update Cart
                </Button>
              ) : (
                <Button
                  component={Link}
                  disabled={!lineItems.length || isCheckoutLoading}
                  loading={isCheckoutLoading}
                  radius="md"
                  size="sm"
                  to="/checkout"
                  onClick={() => {
                    setIsCheckoutLoading(true);
                    $checkout.setKey("cartId", cart.id);
                    setTimeout(() => {
                      CartService.toggleCart(false);
                    }, 5000);
                  }}
                >
                  <Flex align="center" gap="6px">
                    <Text fw={700} size="sm">
                      {formatPrice(subtotal)}
                    </Text>
                    <Text size="sm">|</Text>
                    <Text size="sm">Checkout</Text>
                  </Flex>
                </Button>
              )}
            </div>
          </>
        ) : (
          <div className="flex flex-wrap justify-between gap-2">
            <Button
              onClick={uploadModal.open}
              color="gray.5"
              radius="md"
              size="sm"
            >
              Create another print!
            </Button>
            {isPendingUpdate ? (
              <Button
                radius="md"
                size="sm"
                onClick={updateLineItem}
                loading={updateLineItem.isPending}
              >
                Update Cart
              </Button>
            ) : (
              <Button
                component={Link}
                disabled={!lineItems.length || isCheckoutLoading}
                loading={isCheckoutLoading}
                radius="md"
                size="sm"
                to="/checkout"
                onClick={() => {
                  setIsCheckoutLoading(true);
                  $checkout.setKey("cartId", cart.id);
                  setTimeout(() => {
                    CartService.toggleCart(false);
                  }, 5000);
                }}
              >
                <Flex align="center" gap="6px">
                  <Text fw={700} size="sm">
                    {formatPrice(subtotal)}
                  </Text>
                  <Text size="sm">|</Text>
                  <Text size="sm">Checkout</Text>
                </Flex>
              </Button>
            )}
          </div>
        )}
        {/* <div className="flex flex-wrap justify-between gap-2">
          <Button
            onClick={uploadModal.open}
            color="gray.5"
            radius="md"
            size="sm"
          >
            Create another print!
          </Button>
          {isPendingUpdate ? (
            <Button
              radius="md"
              size="sm"
              onClick={updateLineItem}
              loading={updateLineItem.isPending}
            >
              Update Cart
            </Button>
          ) : (
            <Button
              component={Link}
              disabled={!lineItems.length || isCheckoutLoading}
              loading={isCheckoutLoading}
              radius="md"
              size="sm"
              to="/checkout"
              onClick={() => {
                setIsCheckoutLoading(true);
                $checkout.setKey("cartId", cart.id);
                setTimeout(() => {
                  CartService.toggleCart(false);
                }, 5000);
              }}
            >
              <Flex align="center" gap="6px">
                <Text fw={700} size="sm">
                  {formatPrice(subtotal)}
                </Text>
                <Text size="sm">|</Text>
                <Text size="sm">Checkout</Text>
              </Flex>
            </Button>
          )}
          {isAdmin && isShareable && (
            <>
              <Button
                color="indigo"
                radius="md"
                size="sm"
                leftSection={<ShareIcon className="size-4" />}
                onClick={toggleCheckoutLink}
                loading={toggleCheckoutLink.isPending}
                disabled={!lineItems.length}
              >
                {showCheckoutLink
                  ? "Hide checkout link"
                  : "Create checkout link"}
              </Button>
              <Button
                radius="md"
                size="sm"
                leftSection={<PlusIcon className="size-4" />}
                onClick={openCustomItemModal}
              >
                Create Custom Item
              </Button>
            </>
          )}
        </div>
        {isAdmin && showCheckoutLink && generatedCheckoutLink && (
          <div className="grid gap-2 rounded-md border border-[#00000014] bg-white p-2">
            <TextInput value={generatedCheckoutLink} readOnly />
            <Button
              size="xs"
              variant="light"
              color={linkCopied ? "teal" : undefined}
              leftSection={<Clipboard size={14} />}
              onClick={() => {
                navigator.clipboard.writeText(generatedCheckoutLink);
                setLinkCopied(true);
                setTimeout(() => setLinkCopied(false), 2000);
              }}
            >
              {linkCopied ? "Copied!" : "Copy link"}
            </Button>
          </div>
        )}

        {isAdmin && (
          <div className="flex flex-col gap-2 pt-2">
            <Switch
              label="Turn off sales tax"
              checked={!!(cart as any).taxExempt}
              onChange={e => toggleTaxExempt(e.currentTarget.checked)}
              color="#4AA0A0"
              size="sm"
            />
            <Switch
              label="Turn off shipping calculations"
              checked={!!(cart as any).skipShipping}
              onChange={e => toggleSkipShipping(e.currentTarget.checked)}
              color="#4AA0A0"
              size="sm"
            />
          </div>
        )} */}
      </div>
    </div>
  );
}
